import { useState, useEffect } from "react";

const COMMANDS = [
  { cmd: "/value", desc: "Top 5 value bets right now", icon: "🔥" },
  { cmd: "/arb", desc: "Live arbitrage opportunities", icon: "⚡" },
  { cmd: "/bestodds", desc: "Best odds across all bookmakers", icon: "📊" },
  { cmd: "/heatmap", desc: "Where the value is today", icon: "🗺️" },
  { cmd: "/bet P[amount]", desc: "Smart parlay builder — any amount", icon: "🎯" },
  { cmd: "/bet P[amount] conservative", desc: "Low risk parlay", icon: "🟢" },
  { cmd: "/bet P[amount] aggressive", desc: "Higher risk parlay", icon: "🟠" },
  { cmd: "/kelly [odds] [prob%]", desc: "Kelly criterion calculator", icon: "🧮" },
  { cmd: "/clv", desc: "Your closing line value stats", icon: "📈" },
  { cmd: "/link [email]", desc: "Link your platform account", icon: "🔗" },
  { cmd: "/help", desc: "All available commands", icon: "❓" },
];

export default function TelegramBot() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 800, margin: "0 auto", width: "100%" }}>

      {/* Hero */}
      <div className="card" style={{ padding: 28, background: "linear-gradient(135deg, rgba(33,150,243,0.08) 0%, rgba(0,229,176,0.04) 100%)", border: "1px solid rgba(61,142,248,0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ width: 56, height: 56, background: "#2196F3", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20 }}>@OddsEdgeBW_bot</div>
            <div style={{ fontSize: 13, color: "var(--text3)", marginTop: 3 }}>Your betting intelligence in Telegram — value bets, arbs, and parlays on demand</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href="https://t.me/OddsEdgeBW_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ fontSize: 14, padding: "12px 24px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
            </svg>
            Open Bot in Telegram
          </a>
          <a
            href="https://t.me/OddsEdgeBW_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
            style={{ fontSize: 14, padding: "12px 24px", textDecoration: "none" }}
          >
            Start chatting →
          </a>
        </div>
      </div>

      {/* How it works */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 16 }}>How it works</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 14 }}>
          {[
            { num: "1", title: "Open the bot", desc: "Click the button above or search @OddsEdgeBW_bot in Telegram. Press Start." },
            { num: "2", title: "Link your account", desc: "Type /link your@email.com to connect your OddsEdgeBW account and unlock CLV tracking." },
            { num: "3", title: "Get alerts & insights", desc: "Ask for value bets, arbs, or parlays anytime. The bot reads live data from your platform." },
          ].map(s => (
            <div key={s.num} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, color: "#080d17", flexShrink: 0 }}>{s.num}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Commands */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Bot Commands</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {COMMANDS.map(c => (
            <div key={c.cmd} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px", background: "var(--bg3)",
              borderRadius: "var(--radius-sm)",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{c.icon}</span>
              <code style={{
                background: "rgba(0,229,176,0.1)", color: "var(--accent)",
                padding: "3px 8px", borderRadius: 4, fontSize: 12,
                whiteSpace: "nowrap", flexShrink: 0,
              }}>{c.cmd}</code>
              <span style={{ fontSize: 13, color: "var(--text3)" }}>{c.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Parlay risk levels */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Parlay Risk Levels</div>
        <div style={{ fontSize: 13, color: "var(--text3)", marginBottom: 16 }}>
          When using /bet, add a risk level after your amount to get tailored parlay suggestions.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12 }}>
          {[
            { level: "conservative", icon: "🟢", desc: "2-3 legs, lower odds, safer return", example: "/bet P50 conservative" },
            { level: "balanced", icon: "🟡", desc: "3-4 legs, moderate odds and risk", example: "/bet P50 balanced" },
            { level: "aggressive", icon: "🟠", desc: "4-5 legs, higher odds, more risk", example: "/bet P50 aggressive" },
            { level: "longshot", icon: "🔴", desc: "5+ legs, maximum odds, high risk", example: "/bet P50 longshot" },
          ].map(r => (
            <div key={r.level} style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: 14 }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{r.icon}</div>
              <div style={{ fontWeight: 600, fontSize: 13, textTransform: "capitalize", marginBottom: 4 }}>{r.level}</div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8, lineHeight: 1.5 }}>{r.desc}</div>
              <code style={{ fontSize: 10, color: "var(--accent)", background: "rgba(0,229,176,0.08)", padding: "2px 6px", borderRadius: 3 }}>{r.example}</code>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{
        padding: "24px 20px", background: "rgba(33,150,243,0.06)",
        border: "1px solid rgba(61,142,248,0.15)", borderRadius: "var(--radius)",
        textAlign: "center",
      }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
          Ready to start?
        </div>
        <div style={{ fontSize: 13, color: "var(--text3)", marginBottom: 20 }}>
          Open the bot in Telegram and send /start to get going.
        </div>
        <a
          href="https://t.me/OddsEdgeBW_bot"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ fontSize: 14, padding: "12px 28px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
          </svg>
          Open @OddsEdgeBW_bot
        </a>
      </div>

      <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", padding: "4px 0" }}>
        ⚠️ The bot reads live data from your platform. Make sure you're logged in and have linked your account with /link for personalised features.
      </div>
    </div>
  );
}